import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Nav, Row, Tab } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import avatarMale from '../../assets/images/avatar_male.jpeg';
import avatarFemale from '../../assets/images/avatar_female.jpeg';
import WebcamCapture from '../../components/doctors/modals/WebcamCapture';
import BasicPatientForAppointment from '../../components/doctors/partials/basicPatientForAppointment';
import Header from '../../components/doctors/partials/Header';
import MedicalPatientForAppointment from '../../components/doctors/partials/medicalPatientForAppointment';
import OthersPatientInfo from '../../components/doctors/partials/othersPatientInfo';
import TopLeftMenu from '../../components/doctors/partials/topLeftMenu';
import LoadingButton from '../../components/forms/LoadingButton';
import { useGetRequest } from '../../hooks/useGetRequest';
import { usePatchRequest } from '../../hooks/usePatchRequest';
import { useValidation } from '../../hooks/validationHooks/useValiation';
import { deleteRequest } from '../../utils/axiosRequests';
import {
  agePrint,
  capitalizeFirstLetter,
  separateDateTime,
  userRole,
} from '../../utils/helpers';
import { appointmentSchema } from '../../validationSchemas/appointment.validation';
import { ReactComponent as BackArrow } from '../../assets/icons/arrow-square-left.svg';

function AppointmentEdit() {
  const { state } = useLocation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [visitType, setVisitType] = useState('new');
  const [appointmentDateTime, setAppointmentDateTime] = useState('');
  const [visitationFee, setVisitationFee] = useState('');
  const [backendError, setBackendError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOnExamination, setSelectedOnExamination] = useState({});
  const [patientPicture, setPatientPicture] = useState(null);
  const [patientId, setPatientId] = useState('');
  const [patientInfo, setPatientInfo] = useState({});
  const [appointmentInfo, setAppointmentInfo] = useState({});
  const [isOpenCamera, setIsOpenCamera] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [errors, setErrors] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [isOldPatient, setIsOldPatient] = useState(false);
  const [patientType, setPatientType] = useState('');
  const [doctorPatientId, setDoctorPatientId] = useState([]);
  const [selectedInvestigations, setSelectedInvestigations] = useState([]);

  const validation = useValidation;

  const userInfo = useSelector((state) => state.authReducer.data);
  const activeOrganization = useSelector(
    (state) => state.orgReducer.organization,
  );
  const activeDoctor = useSelector(
    (state) => state.doctorsOfAssistantReducer.activeDoctor,
  );
  const patientSettings = useSelector(
    (state) => state?.prescriptionReducer?.prescriptionInfo?.patientSettings,
  );
  const ageProperties = patientSettings?.items?.find(
    (item) => item.name === 'age',
  );
  let { unitProperties } = ageProperties || {};

  let navigate = useNavigate();
  const onSuccess = (data) => {
    toast.success(data.message, {
      position: toast.POSITION.TOP_RIGHT,
    });
    navigate('/appointments');
  };
  const onError = (error) => {
    setLoadingStatus(false);
    setBackendError(error.message);
  };

  useEffect(() => {
    const appointmentInfo = state?.appointmentInfo;
    if (appointmentInfo) {
      setAppointmentInfo(appointmentInfo);
      const patientInfo = appointmentInfo?.patientId;
      setPatientInfo(patientInfo);
      setPhoneNumber(patientInfo?.phoneNumber);
      setSelectedOnExamination({
        ...selectedOnExamination,
        generalExaminations: {
          ...appointmentInfo?.otherDetails?.generalExaminations,
        },
      });
      setVisitType(appointmentInfo?.visitType);
      setPatientType(appointmentInfo?.patientType);
      setPatientId(patientInfo?.id);
      if (Array.isArray(appointmentInfo?.investigations)) {
        setSelectedInvestigations(appointmentInfo.investigations);
      }
    }
  }, []);

  const { isLoading: isProfilePictureLoading, refetch: getProfilePicture } =
    useGetRequest(
      `get-patient-img-01/${patientId}`,
      `auth/patient-picture/${patientId}`,
      (data) => {
        setPatientPicture(data);
        console.log(data);
      },
      (e) => {
        console.log('error message: ', e.message);
      },
    );

  useEffect(() => {
    console.log('getting picturesdfs: ', patientId);
    if (patientId) getProfilePicture();
  }, [patientId]);

  const deletePatientImage = () => {
    if (patientPicture) {
      const confirmDelete = window.confirm(
        'Are you sure you want to delete this image?',
      );
      // Proceed with deletion if confirmed
      if (confirmDelete) {
        deleteRequest(`auth/patient-img-delete/${patientInfo.id}`)
          .then((res) => {
            console.log(res, 'deletePatientImage');
          })
          .catch((error) => {});
      }
    } else {
      setImageSrc(null);
    }
  };

  const { isPatientLoading, mutate: submitUser } = usePatchRequest(
    `appointments/update-appointment-with-patient/${appointmentInfo?.id}`,
    {
      patientId: patientInfo?.id,
      name: patientInfo.name,
      //dob: ageOrDob ? dob : ageConvertToDateTime(dob),
      dob: patientInfo.dob,
      phoneNumber: patientInfo.phoneNumber
        ? patientInfo.phoneNumber
        : phoneNumber,
      gender: patientInfo.gender ? patientInfo.gender : 'male',
      address: patientInfo?.patientDetails?.address
        ? patientInfo?.patientDetails?.address
        : '',
      email: patientInfo?.email ? patientInfo?.email : '',
      appointmentDateTime: appointmentDateTime,
      visitType: visitType,
      serialNumber: patientInfo?.serialNumber
        ? patientInfo?.serialNumber
        : appointmentInfo?.serialNumber,
      fee: visitationFee
        ? parseInt(visitationFee)
        : appointmentInfo?.paymentDetails?.amount,
      generalExaminations: selectedOnExamination?.generalExaminations,
      investigations: selectedInvestigations,
      nid: patientInfo?.patientDetails?.nid,
      reference_by: appointmentInfo?.otherDetails?.reference_by,
      registration_no: patientInfo?.patientDetails?.registration_no,
      disease: appointmentInfo?.otherDetails?.disease,
      image: patientInfo?.image ? patientInfo?.image : null,
      doctorId:
        userRole(userInfo.userType) === 'assistant'
          ? activeDoctor.id
          : userInfo.id,
      organizationId:
        userRole(userInfo.userType) === 'assistant'
          ? userInfo?.organizationIds[0]?.id
          : activeOrganization.id,
      patientType,
    },
    onSuccess,
    onError,
  );
  const handleSubmit = () => {
    setLoadingStatus(true);
    const { isValid, errors } = validation(
      {
        ...patientInfo,
        birthDate: patientInfo?.dob,
        appointmentDateTime: appointmentDateTime,
      },
      appointmentSchema,
    );
    if (isValid) {
      submitUser();
    }
    {
      if (Object.keys(errors).length !== 0) {
        setErrors(errors);
        setLoadingStatus(false);
      }
    }
  };

  const selectSearchResult = (selectedUser) => {
    const user = selectedUser?.[0] || {};
    setPatientInfo({ ...user });
  };

  const getAvatarImage = () => {
    if (imageSrc) return imageSrc;
    else if (patientPicture) return patientPicture;
    else if (patientInfo?.gender === 'female') return avatarFemale;
    else return avatarMale;
  };

  const getPatientIdForDoctor = (idList) => {
    let patientId = '';
    idList?.forEach((item) => {
      if (item.doctorId === userInfo.id) {
        patientId = item.patientId;
      }
    });
    return patientId;
  };

  useEffect(() => {
    const doctorPatientId = getPatientIdForDoctor(
      patientInfo?.patientIdForDoctor,
      userInfo,
    );
    console.log('working for patient Id : test patientId', patientInfo);
    setDoctorPatientId(doctorPatientId);
  }, [patientInfo]);

  return (
    <div>
      <Header />
      <div className="container-fluid">
        <div className="row mb-2">
          <div className="col-md-12 sticky-header shadow header-sticky-second">
            <TopLeftMenu />
          </div>
        </div>
        <Row className="rx-body">
          <div className="col-xl-12 col-md-12 col-lg-12">
            <div className="card shadow mb-4">
              <div className="card-header py-3 d-flex flex-row align-items-center justify-content-between">
                <span style={{ width: '25%' }}>
                  <BackArrow
                    className="trash-icon square-back-arrow cursor-pointer"
                    onClick={() => navigate('/appointments')}
                  />{' '}
                  <Link to={'/appointments'} className="back-link-text">
                    {' '}
                    <span>Back to Appointment List</span>
                  </Link>
                </span>
                <h6
                  className="m-0 font-weight-bold center black"
                  style={{ width: '75%' }}
                >
                  Edit Appointment
                </h6>
              </div>

              <div className="card-body patient-history appointment-card-body">
                <Row>
                  <Col xs={3} sm={3} md={3}></Col>
                  <Col md={12} xs={12} sm={12} className="pl-4">
                    <Form className="appointmentForm mb-4">
                      <div className="v-error text-center">{backendError}</div>
                      <div className="row">
                        <div className="col-xl-12 col-lg-12">
                          <div>
                            <div className="patient-history patient-info">
                              <Row style={{ marginLeft: '.15rem' }}>
                                <Col
                                  md={3}
                                  className="patient-details my-2"
                                  style={{ fontSize: '15px' }}
                                >
                                  <div className="card shadow py-3">
                                    <div className="icon-list">
                                      {(patientPicture || imageSrc) && (
                                        <i
                                          className="far fa-times-circle cursor-pointer pr-3"
                                          onClick={() => deletePatientImage()}
                                        ></i>
                                      )}
                                    </div>
                                    <div className="patient-img-info">
                                      {!isOpenCamera ? (
                                        <img
                                          className="profile-img"
                                          alt="Avatar"
                                          src={
                                            patientInfo?.gender === 'female'
                                              ? getAvatarImage()
                                              : getAvatarImage()
                                          }
                                        />
                                      ) : (
                                        <>
                                          <WebcamCapture
                                            imageSrc={imageSrc}
                                            setImageSrc={setImageSrc}
                                            isOpenCamera={isOpenCamera}
                                            patientInfo={patientInfo}
                                            setPatientInfo={setPatientInfo}
                                            setIsOpenCamera={setIsOpenCamera}
                                          />
                                        </>
                                      )}
                                      <div className="patient-name">
                                        {patientInfo?.name}
                                      </div>
                                      {/* <div className="patient-phone-number">{patientInfo?.phoneNumber}</div> */}
                                      {/* <div className="patient-email light-blue-color">
                                        {patientInfo?.email}
                                      </div> */}
                                      <div className="patient-pid mt-2">
                                        <span>Patient ID:</span>
                                        {doctorPatientId}
                                      </div>
                                      <div className="patient-age">
                                        <span>Age:</span>
                                        {agePrint(
                                          separateDateTime(patientInfo?.dob),
                                          unitProperties,
                                        )}
                                      </div>
                                      <div className="center">
                                        <button
                                          className="small-btn mt-2 mb-3"
                                          onClick={(e) => {
                                            setIsOpenCamera(true);
                                            e.preventDefault();
                                          }}
                                        >
                                          Edit Picture
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="card shadow py-3 my-3 black">
                                    <h4 className="center">Information</h4>
                                    <div>
                                      <span className="information_span pr-2">
                                        Gender:
                                      </span>
                                      {capitalizeFirstLetter(
                                        patientInfo?.gender,
                                      )}
                                    </div>
                                    <div>
                                      <span className="information_span pr-2">
                                        Height:
                                      </span>
                                      {selectedOnExamination
                                        ?.generalExaminations?.heightInfo
                                        ?.feet || 'n/a'}{' '}
                                      {selectedOnExamination
                                        ?.generalExaminations?.heightInfo?.feet
                                        ? 'Ft '
                                        : ''}{' '}
                                      {selectedOnExamination
                                        ?.generalExaminations?.heightInfo
                                        ?.inch || ''}{' '}
                                      {selectedOnExamination.generalExaminations
                                        ?.heightInfo?.inch
                                        ? ' inch'
                                        : ''}
                                    </div>
                                    <div>
                                      <span className="information_span pr-2">
                                        Weight:
                                      </span>
                                      {selectedOnExamination
                                        ?.generalExaminations?.weightInfo
                                        ?.weight
                                        ? selectedOnExamination
                                            .generalExaminations?.weightInfo
                                            ?.weight +
                                          ' ' +
                                          selectedOnExamination
                                            .generalExaminations?.weightInfo
                                            ?.weightUnit
                                        : 'n/a'}
                                    </div>
                                    <div>
                                      <span className="information_span pr-2">
                                        Phone No:
                                      </span>
                                      {patientInfo?.phoneNumber
                                        ? patientInfo?.phoneNumber
                                        : 'n/a'}
                                    </div>
                                    <div>
                                      <span className="information_span pr-2">
                                        Blood Group:{' '}
                                      </span>
                                      {selectedOnExamination
                                        ?.generalExaminations?.bloodGroup ||
                                        'n/a'}
                                    </div>
                                  </div>
                                  {/* <div className="icon-list">
                                    <i
                                      className="fa fa-plus-circle"
                                      onClick={() => setIsOpenCamera(true)}
                                    ></i>
                                    <i
                                      className="fa fa-edit"
                                      onClick={() => setIsOpenCamera(true)}
                                    ></i>
                                    {(patientPicture || imageSrc) && (
                                      <i
                                        className="far fa-times-circle cursor-pointer"
                                        onClick={() => deletePatientImage()}
                                      ></i>
                                    )}
                                  </div>
                                  <div className="patient-img-info">
                                    {!isOpenCamera ? (
                                      <img
                                        className="profile-img"
                                        src={
                                          imageSrc ||
                                          patientPicture ||
                                          profileImg
                                        }
                                      />
                                    ) : (
                                      <>
                                        <WebcamCapture
                                          imageSrc={imageSrc}
                                          setImageSrc={setImageSrc}
                                          isOpenCamera={isOpenCamera}
                                          patientInfo={patientInfo}
                                          setPatientInfo={setPatientInfo}
                                          setIsOpenCamera={setIsOpenCamera}
                                        />
                                      </>
                                    )}
                                    <div className="font-weight-bold mt-2">
                                      {patientInfo?.name}
                                    </div>
                                    <div>{patientInfo?.phoneNumber}</div>
                                    <div>{patientInfo?.email}</div>
                                  </div> */}
                                  {/* <hr /> */}
                                </Col>
                                {/* <Row className="text-center">
                                    <Col>
                                      <Row>
                                        <Col>
                                          <div>Gender</div>
                                          <div className="font-weight-bold mt-2">
                                            {capitalizeFirstLetter(
                                              patientInfo?.gender,
                                            )}
                                          </div>
                                          <hr />
                                        </Col>
                                        <Col>
                                          <div>Age</div>
                                          <div className="font-weight-bold mt-2">
                                            {agePrint(
                                              separateDateTime(
                                                patientInfo?.dob,
                                              ),
                                              {
                                                years: {
                                                  enabled: true,
                                                  label: 'Years',
                                                },
                                              },
                                            )}
                                          </div>
                                          <hr />
                                        </Col>
                                      </Row>
                                      <Row>
                                        <Col>
                                          <div>Height</div>
                                          <div className="font-weight-bold mt-2">
                                            {
                                              selectedOnExamination
                                                ?.generalExaminations
                                                ?.heightInfo?.feet
                                            }
                                            {selectedOnExamination
                                              ?.generalExaminations?.heightInfo
                                              ?.inch
                                              ? '.' +
                                                selectedOnExamination
                                                  .generalExaminations
                                                  ?.heightInfo?.inch
                                              : ''}
                                            {selectedOnExamination
                                              ?.generalExaminations?.heightInfo
                                              ?.feet ||
                                            selectedOnExamination
                                              .generalExaminations?.heightInfo
                                              ?.inch
                                              ? ' ' +
                                                selectedOnExamination
                                                  .generalExaminations
                                                  ?.heightInfo?.heightUnit
                                              : ''}
                                          </div>
                                          <hr />
                                        </Col>
                                        <Col>
                                          <div>Weight</div>
                                          <div className="font-weight-bold mt-2">
                                            {selectedOnExamination
                                              ?.generalExaminations?.weightInfo
                                              ?.weight
                                              ? selectedOnExamination
                                                  .generalExaminations
                                                  ?.weightInfo?.weight +
                                                ' ' +
                                                selectedOnExamination
                                                  .generalExaminations
                                                  ?.weightInfo?.weightUnit
                                              : ''}
                                          </div>
                                          <hr />
                                        </Col>
                                      </Row>
                                    </Col>
                                  </Row>
                                </Col> */}
                                <Col md={9} className="pl-4">
                                  <Tab.Container
                                    id="left-tabs-example"
                                    defaultActiveKey="basic"
                                  >
                                    <Nav
                                      variant="pills"
                                      className="d-flex custom-tab"
                                      style={{
                                        alignItems: 'center',
                                        textAlign: 'center',
                                      }}
                                    >
                                      <Nav.Item className="flex-fill">
                                        <Nav.Link eventKey="basic">
                                          Appointment Details
                                        </Nav.Link>
                                      </Nav.Item>
                                      {/* <Nav.Item className="flex-fill">
                                        <Nav.Link eventKey="medical">
                                          Medical Information
                                        </Nav.Link>
                                      </Nav.Item>
                                      <Nav.Item className="flex-fill">
                                        <Nav.Link eventKey="others">
                                          Others
                                        </Nav.Link>
                                      </Nav.Item> */}
                                    </Nav>
                                    <Tab.Content>
                                      <Tab.Pane
                                        eventKey="basic"
                                        className="card shadow"
                                        style={{
                                          height: '452px',
                                          overflowY: 'auto',
                                        }}
                                      >
                                        <BasicPatientForAppointment
                                          patientInfo={patientInfo}
                                          setPatientInfo={setPatientInfo}
                                          selectSearchResult={
                                            selectSearchResult
                                          }
                                          setPhoneNumber={setPhoneNumber}
                                          appointmentInfo={appointmentInfo}
                                          setIsOldPatient={setIsOldPatient}
                                          setAppointmentDateTime={
                                            setAppointmentDateTime
                                          }
                                          patientType={patientType}
                                          setPatientType={setPatientType}
                                          setVisitationFee={setVisitationFee}
                                          visitType={visitType}
                                          setVisitType={setVisitType}
                                          isEdit={true}
                                          errors={errors}
                                          selectedInvestigations={
                                            selectedInvestigations
                                          }
                                          setSelectedInvestigations={
                                            setSelectedInvestigations
                                          }
                                        />
                                        <MedicalPatientForAppointment
                                          patientInfo={patientInfo}
                                          setPatientInfo={setPatientInfo}
                                          selectedOnExamination={
                                            selectedOnExamination
                                          }
                                          setSelectedOnExamination={
                                            setSelectedOnExamination
                                          }
                                          appointmentInfo={appointmentInfo}
                                        />
                                        <OthersPatientInfo
                                          patientInfo={patientInfo}
                                          setPatientInfo={setPatientInfo}
                                          appointmentInfo={appointmentInfo}
                                          setAppointmentInfo={
                                            setAppointmentInfo
                                          }
                                        />
                                      </Tab.Pane>
                                      {/* <Tab.Pane eventKey="medical"></Tab.Pane>
                                      <Tab.Pane eventKey="others"></Tab.Pane> */}
                                      <Row className="mt-2 mb-2">
                                        <Col className="text-center">
                                          {isLoading ? (
                                            <LoadingButton btnFull="yes" />
                                          ) : (
                                            <Button
                                              variant="primary"
                                              size="sm"
                                              style={{ width: 'inherit' }}
                                              onClick={() => handleSubmit()}
                                            >
                                              <b>
                                                <i className="far fa-edit"></i>{' '}
                                                Update Appointment
                                              </b>
                                            </Button>
                                          )}
                                        </Col>
                                      </Row>
                                    </Tab.Content>
                                  </Tab.Container>
                                </Col>
                              </Row>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Form>
                  </Col>
                  <Col nd={3}></Col>
                </Row>
              </div>
            </div>
          </div>
        </Row>
      </div>
    </div>
  );
}

export default AppointmentEdit;
